import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  confirmAlgunaCheckoutSession,
  createAlgunaCheckoutSession,
} from './algunaCheckoutSession';
import { AlgunaCheckoutOptions, AlgunaCheckoutPlan } from './types';

const CONFIRM_POLL_MS = 4000; // 4 seconds
// About 2 minutes of polling, at 15 requests a minute, under the 20 a minute limit
const CONFIRM_MAX_POLLS = 30;

type ConfirmationOptions = {
  teamUuid: string;
  sessionId: string | undefined;
  enabled: boolean;
  onConfirmed: () => void;
};

export function useAlgunaCheckoutSession(
  teamUuid: string,
  plan: AlgunaCheckoutPlan,
  options: AlgunaCheckoutOptions | undefined,
  onActive: () => void
) {
  return useQuery({
    // Different options need their own session, because the API keeps them
    // with it
    queryKey: ['algunaCheckoutSession', teamUuid, plan, options],
    queryFn: () => createAlgunaCheckoutSession(teamUuid, plan, options),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    cacheTime: 0,
    onSuccess: (session) => {
      if (session.status === 'active') onActive();
    },
  });
}

export function useAlgunaCheckoutConfirmation({
  teamUuid,
  sessionId,
  enabled,
  onConfirmed,
}: ConfirmationOptions) {
  const [polls, setPolls] = useState(0);
  const exhausted = polls >= CONFIRM_MAX_POLLS;

  useQuery({
    queryKey: ['algunaCheckoutConfirm', teamUuid, sessionId],
    queryFn: () => {
      if (!sessionId) throw new Error('Cannot confirm without a session id');
      return confirmAlgunaCheckoutSession(teamUuid, sessionId);
    },
    enabled: enabled && Boolean(sessionId),
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: (confirmation, query) => {
      if (confirmation?.status === 'active') return false;

      const { dataUpdateCount, errorUpdateCount } = query.state;
      if (dataUpdateCount + errorUpdateCount >= CONFIRM_MAX_POLLS) return false;

      return CONFIRM_POLL_MS;
    },
    onSuccess: (confirmation) => {
      if (confirmation.status === 'active') onConfirmed();
    },
    onSettled: () => setPolls((count) => count + 1),
  });

  return { exhausted };
}
